/*  ========================================================================
    # Sections - Search
    ========================================================================  */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '../../hooks/useTranslation';
import usePagesApi from '../../hooks/usePagesApi';
import usePostsApi from '../../hooks/usePostsApi';
import useContributorsApi from '../../hooks/useContributorsApi';
import useProductionsApi from '../../hooks/useProductionsApi';
import Leporello from '../../sections/Leporello/Leporello';
import PostCard from '../../components/PostCard';
import SearchInput from '../../components/SearchInput';
import ContributorCard from '../../components/ContributorCard';
import ProductionCards from '../../sections/ProductionCards/ProductionCards';

const Search = ({ menuPages, customProductionsButton }) => {
  // Generic
  const scrollAnchor = useRef(null);
  const sectionsRef = useRef([]);
  const _ = useTranslation();

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Productions
  const { loadingProductions, productions } = useProductionsApi({
    per_page: searchQuery !== '' ? -1 : 6,
    search: searchQuery
  });

  // Pages
  const { loadingPages, pages } = usePagesApi({
    per_page: 8,
    search: searchQuery
  });

  // Posts
  const { loadingPosts, posts } = usePostsApi({
    per_page: 8,
    search: searchQuery
  });

  // Contributors
  const { loadingContributors, contributors } = useContributorsApi({
    per_page: 8,
    search: searchQuery
  });

  // ---- TODO ---- Unterseiten berücksichtigen

  const handleKeyDown = (e, sectionIndex) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      let nextSectionIndex = null;
      if (e.key === 'ArrowDown') {
        nextSectionIndex = sectionIndex + 1 < sectionsRef.current.length ? sectionIndex + 1 : null;
      } else if (e.key === 'ArrowUp') {
        nextSectionIndex = sectionIndex > 0 ? sectionIndex - 1 : null;
      }

      if (nextSectionIndex) {
        sectionsRef.current[nextSectionIndex]?.focus();
        // Scroll to the next section
        sectionsRef.current[nextSectionIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  return (
    <>
      <div className='container' ref={scrollAnchor}>
        <h1>{_('page_search_title')}</h1>
        <SearchInput
          formCalss='p-search__search'
          input={searchQuery}
          onSubmit={value => {
            setSearchQuery(value);
            scrollAnchor?.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          placeholder={_('page_search_placeholder')}
          submitTitle={_('page_search_submit')}
          resetTitle={_('page_search_reset')}
        />
      </div>

      {/* Leporello */}
      <section
        className='p-search__leporello'
        tabIndex={0}
        aria-label={_('page_search_section_title', 'Termine')}
        onKeyDown={e => handleKeyDown(e, 0)}
        ref={element => (sectionsRef.current[0] = element)}
      >
        <Leporello
          title={_('page_search_leporello_title')}
          showMonths={false}
          mode='ITEMS'
          showPerformances={true}
          searchQuery={searchQuery}
          type='all'
          // showSingleEvents={!(productions && searchQuery !== '')}
          showSingleEvents={true}
          numberOfInitialResults={3}
          includedProductions={
            productions && searchQuery !== '' ? productions.map(prod => prod.id) : null
          }
        />
      </section>

      {/* Productions */}
      <section
        className='p-search__productions'
        tabIndex={0}
        aria-label={_('page_search_section_title', 'Produktionen')}
        onKeyDown={e => handleKeyDown(e, 1)}
        ref={element => (sectionsRef.current[1] = element)}
      >
        {loadingProductions ? (
          <div className='container'>
            <div className='p-search__collection'>
              <h2>{_('page_search_productions_title')}</h2>
              <div className='p-search__collection-grid'>
                {[...Array(8)].map((value, index) => (
                  <PostCard key={index} skeleton={true} />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {productions.length > 0 && !loadingProductions ? (
          <>
            <ProductionCards productions={productions} title={_('page_search_productions_title')} />
            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <a
                href={customProductionsButton.url}
                target={customProductionsButton.target}
                aria-label={customProductionsButton.title}
                className='c-button'
              >
                {customProductionsButton.title}
              </a>
            </div>
          </>
        ) : null}
      </section>

      {/* Pages */}
      <section
        className='p-search__pages'
        tabIndex={0}
        aria-label={_('page_search_section_title', 'Seiten')}
        onKeyDown={e => handleKeyDown(e, 2)}
        ref={element => (sectionsRef.current[2] = element)}
      >
        <div className='container'>
          {loadingPages ? (
            <div className='p-search__collection'>
              <h2 className='p-search__collection-title'>{_('page_search_pages_title')}</h2>
              <div className='p-search__collection-grid'>
                {[...Array(8)].map((value, index) => (
                  <PostCard key={index} skeleton={true} />
                ))}
              </div>
            </div>
          ) : null}
          {!loadingPosts &&
            searchQuery === '' &&
            Object.values(menuPages).map(page => (
              <div className='p-search__collection' key={page.id}>
                <h2 className='p-search__collection-title'>{page.title}</h2>
                <div className='p-search__collection-grid'>
                  <PostCard
                    image={page.image}
                    title={page.title}
                    date={null}
                    excerpt={page.excerpt}
                    url={page.url}
                    linkText={_('posts_link_learn_more')}
                  />
                  {page.subpages.map(subpage => (
                    <PostCard
                      key={subpage.id}
                      image={subpage.image}
                      title={subpage.title}
                      date={null}
                      excerpt={subpage.excerpt}
                      url={subpage.url}
                      linkText={_('posts_link_learn_more')}
                    />
                  ))}
                </div>
              </div>
            ))}
          {!loadingPages && searchQuery !== '' && pages.length > 0 ? (
            <div className='p-search__collection'>
              <h2 className='p-search__collection-title'>{_('page_search_pages_title')}</h2>
              <div className='p-search__collection-grid'>
                {pages.map(page => (
                  <PostCard
                    key={page.id}
                    image={page.image}
                    title={page.title}
                    date={null}
                    excerpt={page.excerpt}
                    url={page.url}
                    linkText={_('posts_link_learn_more')}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {!loadingPages && pages.length === 0 ? (
            <div className='p-search__collection'>
              <h2 className='p-search__collection-title'>{_('page_search_pages_title')}</h2>
              <div className='p-search__no-result'>
                <h3>{_('page_search_no_pages_found')}</h3>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* Blog */}
      <section
        className='p-search__blog'
        tabIndex={0}
        aria-label={_('page_search_section_title', 'Beiträge')}
        onKeyDown={e => handleKeyDown(e, 3)}
        ref={element => (sectionsRef.current[3] = element)}
      >
        <div className='container'>
          <div className='p-search__collection'>
            <h2 className='p-search__collection-title'>{_('page_search_blog_title')}</h2>
            <div className='p-search__collection-grid'>
              {loadingPosts ? (
                <>
                  {[...Array(8)].map((value, index) => (
                    <PostCard key={index} skeleton={true} />
                  ))}
                </>
              ) : null}
              {!loadingPosts &&
                posts.length > 0 &&
                posts.map(post => (
                  <PostCard
                    key={post.id}
                    skeleton={false}
                    {...post}
                    linkText={_('posts_link_learn_more')}
                  />
                ))}
            </div>
            {!loadingPosts && posts.length === 0 ? (
              <div className='p-search__no-result'>
                <h3>{_('posts_no_posts_found')}</h3>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* Contributors */}
      {searchQuery ? (
        <section
          className='p-search__contributors'
          tabIndex={0}
          aria-label={_('page_search_section_title', 'Mitarbeiter')}
          onKeyDown={e => handleKeyDown(e, 4)}
          ref={element => (sectionsRef.current[4] = element)}
        >
          <div className='container'>
            <div className='p-search__collection'>
              <h2 className='p-search__collection-title'>{_('page_search_contributors_title')}</h2>
              <div className='p-search__collection-grid'>
                {loadingContributors ? (
                  <>
                    {[...Array(8)].map((value, index) => (
                      <PostCard key={index} skeleton={true} />
                    ))}
                  </>
                ) : null}
                {!loadingContributors &&
                  contributors.length > 0 &&
                  contributors.map((contributor, index) => (
                    <ContributorCard
                      key={index}
                      index={index}
                      name={contributor.name}
                      surname={contributor.surname}
                      image={contributor.image}
                      position={contributor.position}
                      permalink={contributor.url}
                    />
                  ))}
              </div>
              {!loadingContributors && contributors.length === 0 ? (
                <div className='p-search__no-result'>
                  <h3>{_('page_search_no_contributors_found')}</h3>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

Search.propTypes = {
  menuPages: PropTypes.object,
  customProductionsButton: PropTypes.object
};

export default Search;
